import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@vimos/ui";
import { directoryCategories } from "@vimos/api-client";
import { canEdit, useAuth } from "../../lib/auth";
import { deleteManualDirectoryStore, fetchDirectoryWorkQueue, updateDirectoryStore } from "./directoryApi";
import {
  DIRECTORY_PAGE_SIZE,
  defaultDirectoryFilters,
  describeStoreState,
  hasActiveFilters,
  saasManagementUrl,
  type DirectoryAdminPatch,
  type DirectoryFilters,
  type DirectoryStore,
} from "./directoryLib";
import { ManualStoreForm } from "./ManualStoreForm";
import { SubmissionsPanel } from "./SubmissionsPanel";
import { InvitationsPanel } from "./InvitationsPanel";
import { ConfirmDialog } from "./ConfirmDialog";
import { ToastStack, useToasts } from "./Toast";

type Density = "compact" | "cozy";

const editorialOptions: Array<{ value: DirectoryStore["editorial_status"]; label: string }> = [
  { value: "visible", label: "Aprobada" },
  { value: "review", label: "En revision" },
  { value: "hidden", label: "Oculta editorial" },
];

const originViews: Array<{ value: DirectoryFilters["origin"]; label: string; countKey: "total" | "saas" | "manual" }> = [
  { value: "all", label: "Todos", countKey: "total" },
  { value: "saas", label: "SaaS", countKey: "saas" },
  { value: "manual", label: "Manuales", countKey: "manual" },
];

const saasAdminBaseUrl = import.meta.env.VITE_SAAS_ADMIN_BASE_URL as string | undefined;

const selectClass =
  "border-sand/60 bg-paper text-ink h-10 rounded-md border px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-50";
const searchClass =
  "border-sand/60 bg-paper text-ink placeholder:text-mist h-10 w-full rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold";

/** Controles compactos para las celdas de la tabla (misma familia visual). */
const cellFieldClass =
  "border-sand/60 bg-paper text-ink h-8 rounded-md border px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-50";

/** Botones de accion compactos de cada fila (sin depender del tamano del kit). */
const actionButtonBase =
  "inline-flex h-7 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-50";
const actionOutline = `${actionButtonBase} border border-ink/15 text-ink hover:border-ink/30 hover:bg-ink/5`;
const actionPrimary = `${actionButtonBase} bg-signal text-ink hover:bg-signal-hover`;
const actionSecondary = `${actionButtonBase} bg-ink text-paper hover:bg-navy-600`;

/** Botones de icono (acciones secundarias) para mantener estrecha la columna. */
const iconButtonBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-50";
const iconOutline = `${iconButtonBase} border border-ink/15 text-ink hover:border-ink/30 hover:bg-ink/5`;
const iconGhost = `${iconButtonBase} text-ink hover:bg-ink/5`;
const iconPrimary = `${iconButtonBase} bg-signal text-ink hover:bg-signal-hover`;
const iconDanger = `${iconButtonBase} text-red-600 hover:bg-red-50 hover:text-red-700`;

function densityButtonClass(active: boolean): string {
  return `focus-visible:ring-gold px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 ${
    active ? "bg-ink text-paper" : "text-mist hover:text-ink hover:bg-ink/5"
  }`;
}

function StoreAvatar({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        loading="lazy"
        className="border-sand/50 bg-sand/20 h-8 w-8 shrink-0 rounded-md border object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="border-sand/50 bg-sand/20 text-ink/70 h-8 w-8 shrink-0 rounded-md border text-center text-xs font-semibold leading-8"
    >
      {name.slice(0, 1).toUpperCase() || "\u2014"}
    </span>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M10 11v6M14 11v6M5.5 7l1 13h11l1-13M9 7V4h6v3" />
    </svg>
  );
}

function SortOrderInput({
  storeName,
  value,
  disabled,
  onCommit,
}: {
  storeName: string;
  value: number;
  disabled: boolean;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  function commit() {
    const parsed = Number(draft);
    const next = Number.isFinite(parsed) ? parsed : value;
    setDraft(String(next));
    if (next !== value) onCommit(next);
  }

  return (
    <input
      aria-label={`Orden de aparicion de ${storeName}`}
      type="number"
      inputMode="numeric"
      className={`${cellFieldClass} w-16`}
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") setDraft(String(value));
      }}
    />
  );
}

export function DirectoryPage({
  originView = "all",
  section = "directory",
}: {
  originView?: DirectoryFilters["origin"];
  section?: "directory" | "submissions" | "invitations";
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const userCanEdit = canEdit(profile);
  const { toasts, pushSuccess, pushError, dismiss } = useToasts();

  const activeTab = section;
  const [showManualForm, setShowManualForm] = useState(false);
  const [density, setDensity] = useState<Density>("compact");
  const [filters, setFilters] = useState<DirectoryFilters>(() => ({ ...defaultDirectoryFilters, origin: originView }));
  const [page, setPage] = useState(1);
  const [searchForQuery, setSearchForQuery] = useState("");
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    tone: "primary" | "danger";
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    setFilters((current) => current.origin === originView ? current : { ...current, origin: originView });
    setPage(1);
  }, [originView]);

  useEffect(() => {
    if (section !== "directory" || filters.origin === "saas") setShowManualForm(false);
  }, [section, filters.origin]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchForQuery(filters.search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const queryFilters = { ...filters, search: searchForQuery };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["stores_directory", "queue", queryFilters, page],
    queryFn: () => fetchDirectoryWorkQueue(queryFilters, page),
    enabled: section === "directory",
  });
  const stores = useMemo(() => data?.stores ?? [], [data]);
  const hasEditorialColumns =
    data?.supportedColumns.has("is_featured") &&
    data?.supportedColumns.has("sort_order") &&
    data?.supportedColumns.has("editorial_status");

  const stats = data?.stats ?? { total: 0, published: 0, pendingReview: 0, blocked: 0, saas: 0, manual: 0 };
  const totalPages = Math.max(1, Math.ceil((data?.filteredTotal ?? 0) / DIRECTORY_PAGE_SIZE));

  const rowPad = density === "compact" ? "py-1.5" : "py-3";
  const headPad = density === "compact" ? "py-2 text-[11px]" : "py-3 text-xs";
  const cellText = density === "compact" ? "text-xs" : "text-sm";

  function updateFilters(patch: Partial<DirectoryFilters>) {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  }

  const quickUpdate = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: DirectoryAdminPatch; successMessage: string }) =>
      updateDirectoryStore(id, patch),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
      pushSuccess(variables.successMessage);
    },
    onError: (mutationError) => {
      pushError(mutationError instanceof Error ? mutationError.message : "No se pudo completar la accion.");
    },
  });

  const deleteStore = useMutation({
    mutationFn: (store: DirectoryStore) => deleteManualDirectoryStore(store.id),
    onSuccess: (_result, store) => {
      queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
      queryClient.invalidateQueries({ queryKey: ["directory_submissions"] });
      queryClient.invalidateQueries({ queryKey: ["directory_invitations"] });
      pushSuccess(`Se eliminó "${store.name}" del directorio.`);
    },
    onError: (mutationError) => {
      pushError(mutationError instanceof Error ? mutationError.message : "No se pudo eliminar el negocio.");
    },
  });

  function runQuickUpdate(id: string, patch: DirectoryAdminPatch, successMessage: string) {
    quickUpdate.mutate({ id, patch, successMessage });
  }

  function handlePublish(store: DirectoryStore, publish: boolean) {
    const message = publish
      ? `"${store.name}" se publico en /tiendas.`
      : `"${store.name}" se quito del directorio.`;
    runQuickUpdate(store.id, { is_visible_in_directory: publish }, message);
  }

  function handleToggleHidden(store: DirectoryStore) {
    const next = !store.is_force_hidden;
    if (next) {
      setConfirm({
        title: "Bloquear en la web",
        message: `"${store.name}" dejara de aparecer en /tiendas aunque este publicado y aprobado. Puedes desbloquearlo cuando quieras.`,
        confirmLabel: "Ocultar en la web",
        tone: "danger",
        onConfirm: () => {
          setConfirm(null);
          runQuickUpdate(store.id, { is_force_hidden: true }, `"${store.name}" quedo bloqueado en la web.`);
        },
      });
    } else {
      runQuickUpdate(store.id, { is_force_hidden: false }, `"${store.name}" volvio a estar visible.`);
    }
  }

  function handleUnpublish(store: DirectoryStore) {
    setConfirm({
      title: "Quitar del directorio",
      message: `"${store.name}" dejara de aparecer en /tiendas. La revision editorial no cambia; podras volver a publicarlo.`,
      confirmLabel: "Quitar del directorio",
      tone: "primary",
      onConfirm: () => {
        setConfirm(null);
        handlePublish(store, false);
      },
    });
  }

  function handleDeleteManualStore(store: DirectoryStore) {
    if (store.origin !== "manual") return;
    setConfirm({
      title: "Eliminar negocio manual",
      message: `Se eliminará permanentemente "${store.name}" de la base de datos y dejará de aparecer en el directorio. Las invitaciones de edición ligadas a esta ficha también se eliminarán. Las solicitudes que todavía requieren revisión deben resolverse antes. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar permanentemente",
      tone: "danger",
      onConfirm: () => {
        setConfirm(null);
        deleteStore.mutate(store);
      },
    });
  }

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <h1 className="font-display text-ink text-2xl font-bold">
            {activeTab === "submissions" ? "Solicitudes" : activeTab === "invitations" ? "Invitaciones" : "Directorio"}
          </h1>
          <p className="text-mist mt-1 text-sm leading-relaxed">
            {activeTab === "submissions"
              ? "Revisa y resuelve las solicitudes de alta y los cambios propuestos por los propietarios."
              : activeTab === "invitations"
                ? "Genera enlaces de alta y edición, controla su vigencia y revisa qué fichas siguen pendientes."
                : filters.origin === "saas"
              ? "SaaS: la plataforma del propietario es la fuente de datos. Aquí gestionas revisión editorial, clasificación y visibilidad, sin alterar su ficha de origen."
              : filters.origin === "manual"
                ? "Manuales: fichas gestionadas por Vimos de principio a fin, desde el alta y la edición hasta la publicación y los cobros."
                : "Una vista general del directorio. Separa las fichas SaaS de las manuales para trabajar con las reglas correctas de cada origen."}
          </p>
        </div>
        {activeTab === "directory" ? (
          <div className="flex flex-wrap items-center gap-2">
            {hasActiveFilters(filters) ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFilters({ ...defaultDirectoryFilters, origin: originView });
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            ) : null}
            {userCanEdit && filters.origin !== "saas" ? (
              <Button size="sm" onClick={() => setShowManualForm((current) => !current)}>
                {showManualForm ? "Cerrar formulario" : "Anadir negocio"}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {activeTab === "directory" ? <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Total", value: stats.total },
          { label: "Publicados en la web", value: stats.published },
          { label: "En revision", value: stats.pendingReview },
          { label: "Bloqueados", value: stats.blocked },
        ].map((item) => (
          <div key={item.label} className="border-sand/40 bg-paper rounded-lg border px-4 py-3">
            <dt className="text-mist text-xs font-medium uppercase tracking-wide">{item.label}</dt>
            <dd className="font-display text-ink mt-1 text-2xl font-bold">{item.value}</dd>
          </div>
        ))}
      </dl> : null}
      {activeTab === "directory" ? <div className="border-sand/50 bg-paper mt-4 flex flex-wrap items-center gap-2 rounded-lg border p-2" role="group" aria-label="Origen de las fichas">
        {originViews.map((view) => (
          <NavLink
            key={view.value}
            to={view.value === "all" ? "/directorio" : view.value === "saas" ? "/directorio/saas" : "/directorio/manuales"}
            end
            className={({ isActive }) => `inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition ${
              isActive
                ? "bg-slate-900 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {({ isActive }) => <>{view.label}<span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"}`}>{stats[view.countKey]}</span></>}
          </NavLink>
        ))}
        <p className="text-mist ml-1 text-xs">
          {filters.origin === "saas"
            ? "Solo control editorial; la ficha original se modifica en el SaaS."
            : filters.origin === "manual"
              ? "Gestión completa por el equipo de Vimos."
              : "Elige un origen para enfocar la operación."}
        </p>
      </div> : null}
      {activeTab === "directory" && originView === "all" && stats.pendingReview > 0 ? (
        <button
          type="button"
          onClick={() => {
            setFilters({ ...defaultDirectoryFilters, editorial: "review", origin: "all" });
            setPage(1);
            navigate("/directorio");
          }}
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
        >
          Atender {stats.pendingReview} ficha(s) en revisión <span aria-hidden="true">→</span>
        </button>
      ) : null}

      <div>
        {activeTab === "directory" ? (
          <>
            {showManualForm && filters.origin !== "saas" ? (
              <ManualStoreForm
                onCreated={() => {
                  setShowManualForm(false);
                  queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
                  queryClient.invalidateQueries({ queryKey: ["directory_billing_charges"] });
                  queryClient.invalidateQueries({ queryKey: ["directory_billing_accounts"] });
                  pushSuccess("Negocio manual agregado al directorio.");
                }}
                onCancel={() => setShowManualForm(false)}
              />
            ) : null}

            {data && !hasEditorialColumns ? (
              <p className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Migracion pendiente en Supabase: faltan columnas editoriales del directorio. Las acciones de
                publicacion y revision quedan deshabilitadas.
              </p>
            ) : null}

            <div className="border-sand/40 bg-paper mt-5 rounded-lg border p-3 sm:p-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <div className="sm:col-span-2">
                  <label htmlFor="directory-search" className="text-mist mb-1 block text-xs font-medium">
                    Buscar
                  </label>
                  <input
                    id="directory-search"
                    type="search"
                    className={searchClass}
                    placeholder="Nombre, ciudad o categoria"
                    value={filters.search}
                    onChange={(event) => updateFilters({ search: event.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="directory-visibility" className="text-mist mb-1 block text-xs font-medium">
                    Visibilidad
                  </label>
                  <select
                    id="directory-visibility"
                    className={`${selectClass} w-full`}
                    value={filters.visibility}
                    onChange={(event) =>
                      updateFilters({ visibility: event.target.value as DirectoryFilters["visibility"] })
                    }
                  >
                    <option value="all">Todas</option>
                    <option value="visible">Visible en la web</option>
                    <option value="unpublished">Sin publicar</option>
                    <option value="blocked">Bloqueados</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="directory-category" className="text-mist mb-1 block text-xs font-medium">
                    Categoria
                  </label>
                  <select
                    id="directory-category"
                    className={`${selectClass} w-full`}
                    value={filters.category}
                    onChange={(event) => updateFilters({ category: event.target.value })}
                  >
                    <option value="">Todas</option>
                    {directoryCategories.map((category) =>
                      category.children.length === 0 ? (
                        <option key={category.id} value={category.id}>
                          {category.label}
                        </option>
                      ) : (
                        <optgroup key={category.id} label={category.label}>
                          <option value={category.id}>{category.label} (toda la categoria)</option>
                          {category.children.map((child) => (
                            <option key={child.id} value={child.id}>
                              {child.label}
                            </option>
                          ))}
                        </optgroup>
                      ),
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="directory-editorial" className="text-mist mb-1 block text-xs font-medium">
                    Revision
                  </label>
                  <select
                    id="directory-editorial"
                    className={`${selectClass} w-full`}
                    value={filters.editorial}
                    onChange={(event) =>
                      updateFilters({ editorial: event.target.value as DirectoryFilters["editorial"] })
                    }
                  >
                    <option value="all">Todas</option>
                    <option value="visible">Aprobada</option>
                    <option value="review">En revision</option>
                    <option value="hidden">Oculta editorial</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="directory-sort" className="text-mist mb-1 block text-xs font-medium">
                    Ordenar por
                  </label>
                  <select
                    id="directory-sort"
                    className={`${selectClass} w-full`}
                    value={filters.sort}
                    onChange={(event) =>
                      updateFilters({ sort: event.target.value as DirectoryFilters["sort"] })
                    }
                  >
                    <option value="featured">Prioridad</option>
                    <option value="name_asc">Nombre (A-Z)</option>
                    <option value="name_desc">Nombre (Z-A)</option>
                    <option value="recent">Mas recientes</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-mist text-xs" role="status">
                  {data?.filteredTotal ?? 0} resultados · {stats.total} negocios en total
                </p>
                <div
                  role="group"
                  aria-label="Densidad de la tabla"
                  className="border-sand/60 inline-flex overflow-hidden rounded-md border"
                >
                  <button
                    type="button"
                    aria-pressed={density === "compact"}
                    onClick={() => setDensity("compact")}
                    className={densityButtonClass(density === "compact")}
                  >
                    Compacta
                  </button>
                  <button
                    type="button"
                    aria-pressed={density === "cozy"}
                    onClick={() => setDensity("cozy")}
                    className={densityButtonClass(density === "cozy")}
                  >
                    Comoda
                  </button>
                </div>
              </div>
            </div>

            {isLoading ? <p className="sr-only">Cargando directorio...</p> : null}

            {error ? (
              <div
                role="alert"
                className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700"
              >
                <p>No pudimos cargar el directorio. Revisa tu conexion e intentalo de nuevo.</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => void refetch()}>
                  Reintentar
                </Button>
              </div>
            ) : (
              <div className="border-sand/40 bg-paper mt-4 overflow-x-auto rounded-lg border">
                <table className="w-full border-collapse text-left text-sm">
                  <caption className="sr-only">
                    Negocios del directorio. Cada fila permite aprobar la revision editorial, marcar
                    destacado, ajustar el orden y ejecutar acciones de publicacion.
                  </caption>
                  <thead className="border-sand/40 bg-sand/20 text-mist border-b uppercase tracking-wide">
                    <tr>
                      <th scope="col" className={`px-4 ${headPad} font-medium`}>
                        Negocio
                      </th>
                      <th scope="col" className={`px-3 ${headPad} font-medium`}>
                        Web
                      </th>
                      <th scope="col" className={`px-3 ${headPad} font-medium`}>
                        Revision
                      </th>
                      <th scope="col" className={`px-3 ${headPad} text-center font-medium`}>
                        Dest.
                      </th>
                      <th scope="col" className={`px-3 ${headPad} font-medium`}>
                        Orden
                      </th>
                      <th scope="col" className={`px-3 ${headPad} font-medium`}>
                        Publicacion
                      </th>
                      <th scope="col" className={`px-3 ${headPad} text-right font-medium`}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-sand/30 divide-y">
                    {isLoading ? (
                      [0, 1, 2, 3, 4].map((index) => (
                        <tr key={index} className="animate-pulse">
                          <td className={`px-4 ${rowPad}`}>
                            <div className="flex items-center gap-2.5">
                              <div className="bg-sand/50 h-8 w-8 rounded-md" />
                              <div className="space-y-1.5">
                                <div className="bg-sand/50 h-4 w-40 rounded" />
                                <div className="bg-sand/50 h-3 w-24 rounded" />
                              </div>
                            </div>
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 h-4 w-28 rounded-full" />
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 h-7 w-28 rounded-md" />
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 mx-auto h-4 w-4 rounded" />
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 h-7 w-16 rounded-md" />
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 h-7 w-20 rounded-md" />
                          </td>
                          <td className={`px-3 ${rowPad}`}>
                            <div className="bg-sand/50 ml-auto h-7 w-24 rounded-md" />
                          </td>
                        </tr>
                      ))
                    ) : data?.stats.total === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center">
                          <p className="text-ink font-medium">Todavia no hay negocios en el directorio.</p>
                          <p className="text-mist mt-1 text-sm">
                            Agrega un negocio manual o invita a un propietario desde la pestana Invitaciones.
                          </p>
                        </td>
                      </tr>
                    ) : (data?.filteredTotal ?? 0) === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center">
                          <p className="text-ink font-medium">Ningun negocio coincide con los filtros.</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-3"
                            onClick={() => {
                              setFilters({ ...defaultDirectoryFilters, origin: originView });
                              setPage(1);
                            }}
                          >
                            Limpiar filtros
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      stores.map((store) => {
                        const state = describeStoreState(store);
                        const controlsDisabled = !userCanEdit || !hasEditorialColumns;
                        const saasUrl = saasManagementUrl(store, saasAdminBaseUrl);
                        const isManual = store.origin === "manual";
                        return (
                          <tr key={store.id} className="hover:bg-sand/20 transition-colors">
                            <td className={`px-4 ${rowPad}`}>
                              <div className="flex items-center gap-2.5">
                                <StoreAvatar name={store.name} logoUrl={store.logo_url} />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span
                                      className={`text-ink max-w-[18rem] truncate font-medium ${cellText}`}
                                      title={store.slug ? `${store.name} · ${store.slug}` : store.name}
                                    >
                                      {store.name}
                                    </span>
                                    {store.is_featured ? (
                                      <span className="text-gold" title="Destacado" aria-label="Destacado">
                                        &#9733;
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-mist flex items-center gap-1.5 text-[11px]">
                                    <span
                                      className={`inline-flex items-center rounded-full px-1.5 py-px font-mono text-[10px] uppercase tracking-wide ${
                                        isManual
                                          ? "bg-signal-subtle text-signal-hover"
                                          : "bg-sand/40 text-ink"
                                      }`}
                                    >
                                      {isManual ? "Manual" : "SaaS"}
                                    </span>
                                    <span className="max-w-[18rem] truncate">
                                      {[store.city, store.category].filter(Boolean).join(" · ") || "\u2014"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={`px-3 ${rowPad}`}>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-xs uppercase tracking-wide ${
                                  state.isWebVisible
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                                title={state.webDetail}
                                aria-label={`${state.webLabel}. ${state.webDetail}`}
                              >
                                {state.isWebVisible ? "Visible" : "No visible"}
                              </span>
                            </td>
                            <td className={`px-3 ${rowPad}`}>
                              <select
                                aria-label={`Estado de revision de ${store.name}`}
                                className={cellFieldClass}
                                value={store.editorial_status}
                                disabled={controlsDisabled}
                                onChange={(event) =>
                                  runQuickUpdate(
                                    store.id,
                                    {
                                      editorial_status: event.target
                                        .value as DirectoryStore["editorial_status"],
                                    },
                                    `Revision de "${store.name}" actualizada.`,
                                  )
                                }
                              >
                                {editorialOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className={`px-3 ${rowPad} text-center`}>
                              <input
                                type="checkbox"
                                aria-label={`Destacar ${store.name}`}
                                className="h-4 w-4 accent-[#C9A24D]"
                                checked={store.is_featured}
                                disabled={controlsDisabled}
                                onChange={(event) =>
                                  runQuickUpdate(
                                    store.id,
                                    { is_featured: event.target.checked },
                                    event.target.checked
                                      ? `"${store.name}" quedo destacado.`
                                      : `"${store.name}" dejo de estar destacado.`,
                                  )
                                }
                              />
                            </td>
                            <td className={`px-3 ${rowPad}`}>
                              <SortOrderInput
                                storeName={store.name}
                                value={store.sort_order}
                                disabled={controlsDisabled}
                                onCommit={(next) =>
                                  runQuickUpdate(
                                    store.id,
                                    { sort_order: next },
                                    `Orden de "${store.name}" actualizado.`,
                                  )
                                }
                              />
                            </td>
                            <td className={`px-3 ${rowPad}`}>
                              {isManual ? (
                                userCanEdit ? (
                                  !store.is_visible_in_directory && store.editorial_status === "visible" ? (
                                    <button
                                      type="button"
                                      className={actionPrimary}
                                      disabled={quickUpdate.isPending}
                                      onClick={() =>
                                        runQuickUpdate(
                                          store.id,
                                          { is_visible_in_directory: true },
                                          `"${store.name}" se publico en /tiendas.`,
                                        )
                                      }
                                    >
                                      Publicar
                                    </button>
                                  ) : store.is_visible_in_directory ? (
                                    <button
                                      type="button"
                                      className={actionSecondary}
                                      disabled={quickUpdate.isPending}
                                      onClick={() => handleUnpublish(store)}
                                    >
                                      Quitar
                                    </button>
                                  ) : (
                                    <span
                                      className="text-mist max-w-[12rem] text-xs"
                                      title="Aprueba la revision editorial para poder publicar."
                                    >
                                      Aprueba la revision
                                    </span>
                                  )
                                ) : (
                                  <span className="text-mist text-xs">{state.publicationLabel}</span>
                                )
                              ) : (
                                <span
                                  className="text-mist whitespace-nowrap text-xs"
                                  title="La publicacion de esta tienda la gobierna el SaaS; aqui solo controlas la revision editorial y el bloqueo."
                                >
                                  Por SaaS
                                </span>
                              )}
                            </td>
                            <td className={`px-3 ${rowPad}`}>
                              <div className="flex items-center justify-end gap-1.5">
                                <Link to={`/directorio/${store.id}`} className={actionOutline}>
                                  Ficha 360
                                </Link>
                                <Link to={`/directorio/${store.id}/editar`} className={actionOutline}>
                                  Editar
                                </Link>
                                {isManual ? (
                                  <Link
                                    to={`/invitaciones?mode=edit&store_id=${store.id}`}
                                    className={actionOutline}
                                    title={`Generar un enlace de edición para ${store.name}`}
                                  >
                                    Enlace de edición
                                  </Link>
                                ) : null}
                                {saasUrl ? (
                                  <a
                                    href={saasUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={iconOutline}
                                    title="Editar en el SaaS"
                                    aria-label="Editar en el SaaS (nueva pestana)"
                                  >
                                    <ExternalLinkIcon />
                                  </a>
                                ) : null}
                                <button
                                  type="button"
                                  className={store.is_force_hidden ? iconPrimary : iconGhost}
                                  disabled={controlsDisabled}
                                  title={
                                    store.is_force_hidden ? "Desbloquear en la web" : "Ocultar en la web"
                                  }
                                  aria-label={
                                    store.is_force_hidden
                                      ? `Desbloquear ${store.name} en la web`
                                      : `Ocultar ${store.name} en la web`
                                  }
                                  aria-pressed={store.is_force_hidden}
                                  onClick={() => handleToggleHidden(store)}
                                >
                                  {store.is_force_hidden ? <EyeIcon /> : <EyeOffIcon />}
                                </button>
                                {isManual && userCanEdit ? (
                                  <button
                                    type="button"
                                    className={iconDanger}
                                    disabled={deleteStore.isPending}
                                    title="Eliminar negocio manual"
                                    aria-label={`Eliminar ${store.name} permanentemente`}
                                    onClick={() => handleDeleteManualStore(store)}
                                  >
                                    <TrashIcon />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !error && totalPages > 1 ? (
              <nav
                aria-label="Paginacion del directorio"
                className="mt-5 flex items-center justify-between gap-3"
              >
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Anterior
                </Button>
                <p className="text-mist text-sm" role="status">
                  Página {page} de {totalPages} · {stores.length} registros cargados
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  Siguiente
                </Button>
              </nav>
            ) : null}
          </>
        ) : null}

        {activeTab === "submissions" ? <SubmissionsPanel /> : null}
        {activeTab === "invitations" ? <InvitationsPanel /> : null}
      </div>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ""}
        message={confirm?.message ?? ""}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        isLoading={quickUpdate.isPending || deleteStore.isPending}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
